import { useQuery } from "@apollo/client";
import { Avatar, Box, Center, chakra, Divider, Flex, HStack, Skeleton, SkeletonText, Text, VStack } from "@chakra-ui/react";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { Fragment } from "react";
import { BsArrow90DegUp } from "react-icons/bs";
import { FaRegCompass } from "react-icons/fa";
import { HiOutlineBell, HiOutlineChat } from "react-icons/hi";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { GET_JOINED_CLASSES } from "../../graphql/queries/class";
import { activeClass, authData } from "../../State";
import NavCollapseButton from "../UI/Navbar/NavCollapseButton";
import ProfileOptions from "../UI/Navbar/ProfileOptions";
import SideNavButton from "../UI/Navbar/SideNavButton";

interface Props {
  classesData?: any;
  isLoading?: boolean;
  error?: any;
}

interface ClassProps {
  class_name: string;
  _id: string;
  avatar: string;
}

const ActionLinks = ({ classesData = [], isLoading, error }: Props) => {
  //
  const [showClasses, setShowClasses] = React.useState(true);

  // nextjs router
  const router = useRouter();

  // Graphql
  const { data, loading } = useQuery(GET_JOINED_CLASSES, {
    fetchPolicy: "cache-first",
    onError: () => {
      return null;
    },
  });
  const joinedClassesData = data ? data.get_student_profile.joined_classes : {};
  const waitingList = data ? data.get_student_profile.wait_list : {};
  const studentName = data ? data.get_student_profile.full_name : "";

  // recoil
  const { studentData } = useRecoilValue(authData);
  const { active_class_name } = useRecoilValue(activeClass);
  const setActiveClass = useSetRecoilState(activeClass);

  // Routing function, just pass in the pathname e.g "/, /discover etc"
  const handleClassesToggle = () => setShowClasses(!showClasses);

  // const joinedClassesAData = joinedClassesData || studentData.joined_classes;

  // console.log(joinedClassesData)
  // console.log(waitingList)

  let filteredClasses = [];
  let restructuredWaitingList = [];

  if (!loading && joinedClassesData.length > 0) {
    filteredClasses = joinedClassesData.filter((singleClass) => {
      return singleClass.class !== null;
    });
  }

  if (!loading && waitingList.length > 0) {
    // restructure the waiting list to match enrolled classes
    waitingList.forEach((classData) => {
      restructuredWaitingList.push({
        class: {
          ...classData,
        },
      });
    });
  }

  let classesToDisplay = [];

  if (!loading) {
    classesToDisplay = [...restructuredWaitingList, ...filteredClasses];
  }

  //
  const enableStyles = router.pathname.startsWith("/classes/enrolled/");

  let test = [];
  // waitingList &&
  //   waitingList.forEach((classData) => {
  //     test.push({
  //       class: {
  //         ...classData,
  //       },
  //     });
  //   });

  return (
    <Fragment>
      <Flex flexDir="column" h="100vh" pos="relative">
        <Box>
          {/* App logo */}
          <Flex flexDir="column" px={12} height="80px" cursor="pointer">
            <Link href="/">
              <chakra.svg w="48px" h="auto" viewBox="0 0 512 512" my="auto">
                <path
                  d="M140.114,80.528,76.9,136.492V345.073l86.4,86.4H371.884l63.21-55.964H226.512l-84.28-84.28Z"
                  fill="#070707"
                  fillRule="evenodd"
                />
                <path
                  d="M140.114,80.528,76.9,136.492V345.073l86.4,86.4H371.884l63.21-55.964H226.512l-84.28-84.28ZM123.156,118.7,94.247,144.3V337.89l76.239,76.242H365.309l24.04-21.281H226.512a17.355,17.355,0,0,1-12.263-5.08l-84.28-84.28a17.338,17.338,0,0,1-5.076-12.088Z"
                  fill="#070707"
                  fillRule="evenodd"
                />
                <path
                  d="M140.116,289.117V80.531H389.058L435.1,122.672V375.511H226.516Zm30.348-178.238V276.547l68.622,68.616H404.749V136.037l-27.484-25.158Z"
                  fill="#070707"
                  fillRule="evenodd"
                />
                <path d="M279.324,185.949H239.263v88.892H329.75V237.172H279.324Z" fill="#070707" />
              </chakra.svg>
            </Link>
          </Flex>

          {/* Sidebar Top Links */}
          <Box pb={4} pl={12}>
            <VStack align="stretch">
              {/* Messages */}
              <Link passHref href="/messages">
                <SideNavButton text="Messages" icon={<HiOutlineChat size="24px" />} isActive={router.pathname === "/messages"} />
              </Link>

              {/* Discover */}
              <Link passHref href="/discover">
                <SideNavButton text="Discover" icon={<FaRegCompass size="24px" />} isActive={router.pathname === "/discover"} />
              </Link>

              {/* Notifications */}
              <Link passHref href="/notifications">
                <SideNavButton text="Notifications" icon={<HiOutlineBell size="24px" />} isActive={router.pathname === "/notifications"} />
              </Link>
            </VStack>
          </Box>
        </Box>
        {/* Classes header */}
        <Divider />
        <NavCollapseButton
          isActive={router.pathname.includes("classes")}
          text="Classes"
          show={showClasses}
          textSize="18px"
          handleToggle={handleClassesToggle}
          link="/classes/enrolled"
          pl={12}
          py={6}
        />
        {/* Classes links collapse */}
        {showClasses && (
          <Box transition="all .5s" height="100%" pl={12} overflowY="auto">
            {/* All classes */}
            {!loading && classesToDisplay && classesToDisplay.length > 0 && (
              <VStack spacing={6} pt={2} pb={6} pr={6} align="stretch">
                {classesToDisplay.map((c: { class: ClassProps }, i) => {
                  if (c.class !== null)
                    return (
                      <SideNavButton
                        key={c.class && `${i}${c.class._id}`}
                        text={c.class && c.class.class_name}
                        onClick={() => {
                          router.push(`/classes/enrolled/${c.class && c.class._id}`);

                          setActiveClass({
                            active_class_id: c.class && c.class._id,
                            active_class_name: c.class && c.class.class_name,
                          });
                        }}
                        isActive={enableStyles && active_class_name === (c.class && c.class.class_name)}
                        icon={
                          <Box
                            transition="all .1s"
                            _hover={{
                              borderColor: "blue.500",
                            }}
                            border={
                              enableStyles &&
                              (active_class_name === (c.class && c.class.class_name) ? "2px solid #070707" : "1px solid white")
                            }
                            borderWidth={2}
                            p="1px"
                            borderRadius={12}>
                            <Avatar
                              name={c.class && c.class.class_name}
                              // src={c.class && c.class.avatar}
                              borderRadius={10}
                              // borderColor="#fff"
                              width="42px"
                              height="42px"
                              textColor="white"
                              fontWeight="700"
                              // _after={i === 0 ? doubledBorderStyle : null}
                            />
                          </Box>
                        }
                        borderRightWidth={0}
                        py={0}
                      />
                    );
                })}
              </VStack>
            )}

            {/* If there are no classes available */}
            {/* {!loading && classesToDisplay && classesToDisplay.length === 0 && (
              <Box pt={2} pb={6} pr={6}>
                <Button
                  fontSize="md"
                  size="lg"
                  px={5}
                  fontWeight={700}
                  colorScheme="blue"
                  borderRadius="lg"
                  w="100%"
                  onClick={() => {
                    router.push("/discover");
                  }}
                >
                  Join a Class
                </Button>
              </Box>
            )} */}

            {/* When classes are still fetching, loader basically */}
            {loading && (
              <VStack spacing={6} pt={2} pb={6} pr={6} align="stretch">
                <HStack mt={2}>
                  <Skeleton borderRadius="10px" h="42px" w="60px" />
                  <SkeletonText w="100%" noOfLines={1} />
                </HStack>

                <HStack mt={2}>
                  <Skeleton borderRadius="10px" h="42px" w="60px" />
                  <SkeletonText w="100%" noOfLines={1} />
                </HStack>

                <HStack mt={2}>
                  <Skeleton borderRadius="10px" h="42px" w="60px" />
                  <SkeletonText w="100%" noOfLines={1} />
                </HStack>
              </VStack>
            )}
          </Box>
        )}

        {!showClasses && (
          <Center p={10} color="gray.500" flexDir="column" textAlign="center">
            <Text fontSize="3xl" mb={1}>
              <BsArrow90DegUp />
            </Text>
            <Text fontWeight="bold" fontSize="sm">
              Click to show your classes
            </Text>
          </Center>
        )}

        {/* Bottom links */}
        <Divider mt="auto" />
        <Box bg="white" fontSize="sm" px={12}>
          <VStack spacing={6} align="stretch">
            {/* <SideNavButton
              text="New Updates"
              icon={<HiSparkles color="#F1B500" />}

              py={0}
            /> */}

            {loading && (
              <HStack py={3} mt={2}>
                <Skeleton borderRadius="10px" h="42px" w="60px" />
                <SkeletonText w="100%" noOfLines={1} />
              </HStack>
            )}

            {!loading && <ProfileOptions name={studentData.full_name || studentName} />}
          </VStack>
        </Box>
      </Flex>
    </Fragment>
  );
};

export default ActionLinks;
